const React = require('react');
const T = require('prop-types');
const { Virtualizer } = require('virtua');
const { default: Styled, keyframes, css } = require('styled-components');
const { default: Typography } = require('@mui/material/Typography');
const { transient$Props } = require('utils/styles');
const LoadingScreen = require('components/LoadingScreen');
const { wrapFocusIndicatorForElement } = require('components/FocusIndicator');

const {
    MAX_CONTENT_WIDTH,
    MOBILE_MAX_CONTENT_WIDTH
} = require('utils/constants');

const EXTRA_HEADER_FOOTER_PADDING = 16;

const { default: Classes } = require('./styles.scss');
const ChatItem = require('./Item');
const ChatUtils = require('./utils');

const {
    useRef,
    useState,
    useLayoutEffect,
    useEffect,
    useMemo,
    forwardRef
} = React;

const internals = {};

const ChatList = function ChatList(props) {

    const {
        isInitialRender,
        setIsInitialRender,
        messages,
        onSaveMessage,
        onModerateMessage,
        onPinMessage,
        onRemoveMessage,
        onRemoveOwnMessage,
        onFlagInappropriate,
        onSendPrivateMessage,
        onSendConnectionRequest,
        onAcceptPeer,
        openSimpleDialogWithProps,
        onAddReaction,
        onRemoveReaction,
        updateAlertFunction,
        showModerationControls,
        showNotification,
        rolesInteractions,
        rolePermissions,
        isAnnouncement,
        isGroupChat,
        onRequestMessages,
        headerHeight,
        footerHeight,
        hasPrevPage,
        userId,
        classId,
        usersNameMap,
        enableReactions,
        debug,
        onOpenMediaViewer
    } = props;

    const {
        AllChatsDisplayedContainer,
        AllChatsDisplayedLabel,
        ListContainer,
        ChatItemContainer,
        HeaderContainer,
        SpinnerContainer,
        UlListContainer,
        LiItemComponent
    } = internals;

    // When the user gets this many items away from the top,
    // we'll load more items.
    const LOAD_MORE_ITEMS_THRESHOLD = 20;
    // If the user is this many pixels from the bottom, stick to bottom.
    const FOLLOW_OUTPUT_THRESHOLD = 500;

    const visibleMessages = useMemo(() => {

        return (messages || []).filter((message) => ChatUtils.canViewMessage(message, {
            isModerator: showModerationControls
        }));
    }, [messages, showModerationControls]);

    const virtuaRef = useRef(null);
    const listContainerRef = useRef(null);
    const startFetchedCountRef = useRef(0);
    const ready = useRef(false);
    const isAtBottom = useRef(true);
    const latestMessage = useRef(0);
    const [ariaAssertiveText, setAriaAssertiveText] = useState('');

    const [isLoadingMore, setIsLoadingMore] = useState(false);

    const scrollToBottom = () => {

        if (listContainerRef.current) {
            listContainerRef.current.scrollTop = listContainerRef.current.scrollHeight;
        }
    };

    const scrollToBottomAfterLayout = () => {

        scrollToBottom();

        if (typeof window === 'undefined' || !window.requestAnimationFrame) {
            setTimeout(scrollToBottom, 0);
            return;
        }

        window.requestAnimationFrame(() => {

            scrollToBottom();
            window.requestAnimationFrame(scrollToBottom);
        });

        setTimeout(scrollToBottom, 100);
    };

    useLayoutEffect(() => {

        // Start scroll at the bottom
        scrollToBottomAfterLayout();

        ready.current = true;

        // Leave time for the initial render.
        const timeoutId = setTimeout(() => {

            scrollToBottomAfterLayout();
            setIsInitialRender(false);
        }, 500);

        // Clean up timeout to avoid memory leaks.
        return () => clearTimeout(timeoutId);
    }, []);

    useLayoutEffect(() => {

        setIsLoadingMore(false);

        const latestRenderedMessage = messages[messages.length - 1];
        const shouldFollowOwnNewMessage = !isLoadingMore && latestRenderedMessage?.user?.isMe;

        if (!isInitialRender && (isAtBottom.current || shouldFollowOwnNewMessage)) {
            scrollToBottomAfterLayout();
        }

        if (!latestMessage.current && messages.length > 0) {
            latestMessage.current = messages.length - 1;
        }
        else if (latestMessage.current && latestMessage.current < messages.length - 1) {
            latestMessage.current = messages.length - 1;
            const nextMessage = messages[latestMessage.current];

            if (ChatUtils.canViewMessage(nextMessage, { isModerator: showModerationControls })) {
                setAriaAssertiveText(`New message: ${internals.getMessageAnnouncement(nextMessage)}`);
            }
        }
        else if (latestMessage.current && latestMessage.current > messages.length - 1) {
            latestMessage.current = messages.length - 1;
        }

    }, [isInitialRender, messages.length, footerHeight]);

    const onScroll = async () => {

        if (!ready.current || !listContainerRef.current || !virtuaRef.current) {
            return;
        }

        // Force scroll to bottom while initial render is happening
        if (isInitialRender) {
            scrollToBottom();
            return;
        }

        const count = messages.length - 1;

        if (startFetchedCountRef.current < count && virtuaRef.current.findStartIndex() - LOAD_MORE_ITEMS_THRESHOLD < 0) {
            startFetchedCountRef.current = count;
            setIsLoadingMore(true);

            if (onRequestMessages) {
                await onRequestMessages();
            }
        }

        if (listContainerRef.current.scrollTop >= listContainerRef.current.scrollHeight - listContainerRef.current.offsetHeight - FOLLOW_OUTPUT_THRESHOLD) {
            isAtBottom.current = true;
        }
        else {
            isAtBottom.current = false;
        }

        wrapFocusIndicatorForElement();
    };

    const MemoizedUlListContainer = useMemo(
        () =>

            forwardRef((ulProps, ref) => (

                <UlListContainer
                    {...ulProps}
                    ref={ref}
                    aria-setsize={visibleMessages.length} // Pass messages.length as a stable prop
                    role='log'
                />
            )),
        [visibleMessages.length]
    );

    return (
        <ListContainer
            ref={listContainerRef}
        >
            <HeaderContainer aria-hidden='true'>
                {/* Header Spacer */}
                <div style={{ height: headerHeight + EXTRA_HEADER_FOOTER_PADDING }} />
                <div style={{ padding: '10px', textAlign: 'center' }}>
                    {hasPrevPage && (
                        <SpinnerContainer>
                            <LoadingScreen variant='content' />
                        </SpinnerContainer>
                    )}
                    {!hasPrevPage && (
                        <AllChatsDisplayedContainer>
                            <AllChatsDisplayedLabel>
                                <Typography align={'center'} variant={'body2'}>
                                    All available chats are displayed
                                </Typography>
                            </AllChatsDisplayedLabel>
                        </AllChatsDisplayedContainer>
                    )}
                </div>
            </HeaderContainer>
            <Virtualizer
                as={MemoizedUlListContainer}
                item={LiItemComponent}
                ref={virtuaRef}
                shift={isLoadingMore}
                overscan={4}
                itemSize={83}
                startMargin={100} // header height
                onScroll={onScroll}
            >
                {visibleMessages.map((message, index) => {
                    // Render item content
                    const initDelayBase = 0.012;
                    const initDelayMultiplier = Math.max(visibleMessages.length - 1 - index, 0);

                    // Find the latest message from a different user
                    const isLatestOtherUserMessage = (() => {

                        if (message.user?.isMe) {
                            return false;
                        }

                        for (let i = index + 1; i < visibleMessages.length; ++i) {
                            if (!visibleMessages[i].user?.isMe) {
                                return false;
                            }
                        }

                        return true;
                    })();

                    return (
                        <ChatItemContainer
                            key={message.sid}
                            $isInitialRender={isInitialRender}
                            $delay={
                                isInitialRender
                                    ? Math.max(initDelayBase * initDelayMultiplier, 0)
                                    : 0
                            }
                            $debug={debug}
                        >
                            <ChatItem
                                tabIndex={-1}
                                listIndex={index}
                                onSave={onSaveMessage}
                                classId={classId}
                                onModerate={onModerateMessage}
                                onPin={onPinMessage}
                                onRemove={onRemoveMessage}
                                onRemoveOwn={onRemoveOwnMessage}
                                onFlagInappropriate={onFlagInappropriate}
                                onSendPrivateMessage={onSendPrivateMessage}
                                onSendConnectionRequest={onSendConnectionRequest}
                                onAcceptPeer={onAcceptPeer}
                                openSimpleDialogWithProps={openSimpleDialogWithProps}
                                onAddReaction={onAddReaction}
                                onRemoveReaction={onRemoveReaction}
                                updateAlertFunction={updateAlertFunction}
                                showModerationControls={showModerationControls}
                                showNotification={showNotification}
                                rolesInteractions={rolesInteractions}
                                rolePermissions={rolePermissions}
                                className={Classes.cf}
                                message={message}
                                isAnnouncement={isAnnouncement}
                                isGroupChat={isGroupChat}
                                userId={userId}
                                usersNameMap={usersNameMap}
                                isLatestMessage={isLatestOtherUserMessage}
                                enableReactions={enableReactions}
                                debug={debug}
                                onOpenMediaViewer={onOpenMediaViewer}
                            />
                        </ChatItemContainer>
                    );
                })}
            </Virtualizer>
            <div style={{ height: footerHeight + EXTRA_HEADER_FOOTER_PADDING }} />
            <div
                aria-live='assertive'
                style={{
                    height: 0,
                    overflow: 'hidden',
                    position: 'absolute',
                    bottom: -9999
                }}
            >
                {ariaAssertiveText}
            </div>
        </ListContainer>
    );
};

ChatList.propTypes = {
    isInitialRender: T.bool,
    setIsInitialRender: T.func,
    headerHeight: T.number,
    footerHeight: T.number,
    messages: T.array,
    onSaveMessage: T.func.isRequired,
    onModerateMessage: T.func.isRequired,
    onPinMessage: T.func.isRequired,
    onRemoveMessage: T.func.isRequired,
    onRemoveOwnMessage: T.func.isRequired,
    onRequestMessages: T.func.isRequired,
    onFlagInappropriate: T.func.isRequired,
    onSendPrivateMessage: T.func,
    onSendConnectionRequest: T.func,
    onAcceptPeer: T.func,
    openSimpleDialogWithProps: T.func,
    onAddReaction: T.func.isRequired,
    onRemoveReaction: T.func.isRequired,
    updateAlertFunction: T.func,
    showNotification: T.func,
    rolesInteractions: T.arrayOf(T.shape({
        id: T.number,
        name: T.string,
        label: T.string,
        schoolId: T.number,
        canViewProfile: T.bool,
        canViewInGroup: T.bool,
        canChat: T.bool,
        canChatWithoutConnection: T.bool,
        canSeeConnections: T.bool,
        canSeeUsersGroups: T.bool,
        canSeeSurveyFields: T.bool,
        canSeeExtendedProfile: T.bool
    })),
    rolePermissions: T.shape({
        id: T.number,
        canPostInAnnouncement: T.bool
    }),
    showModerationControls: T.bool,
    isAnnouncement: T.bool,
    isGroupChat: T.bool,
    hasPrevPage: T.bool,
    userId: T.number,
    classId: T.number,
    usersNameMap: T.object,
    enableReactions: T.bool,
    debug: T.bool
};

module.exports = ChatList;

internals.ListContainer = Styled.div`
    width: 100%;
    height: 100%;
    overflow-y: auto;

    // Opt-out of browser's scroll anchoring on header/footer
    // because it will conflict to scroll anchoring of virtualizer
    overflow-anchor: none;

    ul,
    li {
        margin: 0;
        padding: 0;
        list-style: none;
    }
`;

internals.ChatItemContainer = Styled('div', transient$Props)`
    list-style-type: none;
    padding: 0px 3px;

    margin: 0;
    margin: auto !important;

    max-width: ${MAX_CONTENT_WIDTH}px;

    @media (max-width: 600px) {
        max-width: none;
    }

    /* Debug settings */
    ${({ $debug }) => {

        if ($debug) {
            return 'outline: 1px solid red;';
        }
    }}

    /* Inline animations with dynamic translateX */
    ${({ $isInitialRender, $delay }) => {

        if (!$isInitialRender) {
            return '';
        }

        // Define keyframes for opacity and slide-in transform
        const fadeInOpacity = keyframes`
            from { opacity: 0; }
            to { opacity: 1; }
        `;

        return css`
            opacity: 0;
            animation:
                ${fadeInOpacity} 0.25s cubic-bezier(0.25, 0.1, 0.25, 1) forwards ${$delay || 0}s
        `;
    }}

    @media (max-width: 600px) {
        max-width: ${MOBILE_MAX_CONTENT_WIDTH}px;
        margin: 0 auto;
    }
`;

internals.AllChatsDisplayedContainer = Styled.div`
    max-width: ${MAX_CONTENT_WIDTH}px;

    margin-left: auto;
    margin-right: auto;
    margin-bottom: 32px;

    @media (max-width: 600px) {
        max-width: none;
    }
`;

internals.AllChatsDisplayedLabel = Styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    width: fit-content;
    min-height: 26px;
    margin: 10px auto 0 !important;
    padding: 2px 12px;
    border-radius: 999px;
    background: rgba(18, 39, 79, 0.06);
    color: ${({ theme }) => theme.palette.text.primary};
    font-size: 14px;
    line-height: 18px;
    font-weight: 800;
    letter-spacing: -0.015em;
    text-align: center;

    .MuiTypography-root {
        color: inherit;
        font-size: inherit;
        line-height: inherit;
        font-weight: inherit;
        letter-spacing: inherit;
    }
`;


internals.HeaderContainer = Styled('div', transient$Props)`
    position: relative;
    border-bottom: 1px ${({ theme }) => theme.palette.menu.hover};
`;

internals.SpinnerContainer = Styled.div`
    position: relative;
    transform: scale(0.5);
    top: -10px;
    height: 50px;
`;

internals.UlListContainer = Styled.ul``;

internals.getMessageAnnouncement = (message) => {

    if (!message || message.moderationStatus !== 'moderated') {
        return message?.body;
    }

    const notice = ChatUtils.getModeratedMessageNotice({ isAuthor: !!message.user?.isMe });

    if (message.user?.isMe) {
        return notice;
    }

    return `${notice}. ${message.body}`;
};

internals.getMessageAriaLabel = (message, index) => {

    return `Message ${index + 1}: ${internals.getMessageAnnouncement(message)}.`;
};

internals.LiItemComponent = forwardRef((liProps, ref) => {

    const message = liProps?.children?.props?.children?.props?.message;

    return (
        <li
            key={message.sid}
            {...liProps}
            ref={ref}
            tabIndex={-1}
            aria-posinset={liProps.index + 1} // Current item's position
            aria-label={internals.getMessageAriaLabel(message, liProps.index)}
            role='listitem'
        />
    );
});
